import { type QueryClient } from "@tanstack/react-query";

import { canAccessAskPuntt } from "./auth";

import { getGuidelines } from "../services/brand-guidelines";
import { getEnterprises } from "../services/enterprises";

/**
 * Single source of truth for the NON-MG default landing target after
 * authentication. Sites with MG-specific routing (currently
 * `LoginView.tsx`) handle the MG branch separately to preserve the
 * existing divergence where:
 *
 *  - `/` (indexRoute) sends MG → `/ask-puntt` (via canAccessAskPuntt)
 *  - `/login` already-authed sends MG → `/roi`
 *
 * Callers MUST honor any explicit redirect (share-link `redirectTo`,
 * login `?redirect=`) BEFORE consulting this helper. The helper only
 * answers "where does a freshly-authed user go when no explicit target
 * is set?"
 *
 * The synchronous variant is safe ONLY from sites where the
 * `brand-guidelines` and `enterprises` query caches are guaranteed
 * primed — routes under `authLayoutRoute` (whose loader primes both at
 * `routes/auth-layout/route.tsx:65-70` and `:111-116`).
 *
 * Outside that layout, use `ensureDefaultPostAuthLandingPath()`.
 *
 * Path strings are inlined rather than imported from the route objects
 * to avoid a TDZ-cycle (rootRoute → unauthLayout → this module →
 * askPunttRoute → authLayoutRoute → rootRoute). See
 * `routes/vitalora-home/view.tsx:187-200` for the established
 * precedent in this codebase.
 */
export const ASK_PUNTT_PATH = "/ask-puntt";
export const TICKETS_PATH = "/tickets";

export type DefaultPostAuthLanding = "ask-puntt" | "tickets";

export function defaultPostAuthLandingPath(): string {
  return canAccessAskPuntt() ? ASK_PUNTT_PATH : TICKETS_PATH;
}

export function defaultPostAuthLandingKind(): DefaultPostAuthLanding {
  return canAccessAskPuntt() ? "ask-puntt" : "tickets";
}

/**
 * Async variant for call sites outside the auth layout. Primes both
 * caches before resolving the path. Silently degrades on fetch failure
 * (cache miss → `canAccessAskPuntt()` returns false → resolves to
 * `/tickets`), matching the auth-layout loader's own error-swallowing
 * behavior.
 */
export async function ensureDefaultPostAuthLandingPath(
  queryClient: QueryClient,
): Promise<string> {
  await Promise.all([
    queryClient
      .ensureQueryData({ queryKey: ["enterprises"], queryFn: getEnterprises })
      .catch(() => undefined),
    queryClient
      .ensureQueryData({
        queryKey: ["brand-guidelines"],
        queryFn: getGuidelines,
      })
      .catch(() => undefined),
  ]);
  return defaultPostAuthLandingPath();
}
